import gsap from "@/lib/gsap";
import { PropsWithChildren, RefObject, useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { Body, Container, Indicator, Marker, Title } from "./styles";

type Props = PropsWithChildren<{
  count: string;
  title: string;
  timeline: gsap.core.Timeline;
  counterValueRef: RefObject<HTMLSpanElement>;
}>;

const Paragraph: React.FC<Props> = (props) => {
  const { title, count, children, timeline, counterValueRef } = props;
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Create closure around mutable refs
    const container = containerRef.current;
    const title = titleRef.current;
    const indicator = indicatorRef.current;
    const counterValue = counterValueRef.current;
    const body = bodyRef.current;

    const updateCounterValue = () => {
      if (counterValue) {
        gsap.to(counterValue, {
          text: {
            value: count,
          },
        });
      }
    };

    const animation = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        scrub: 1,
        start: "top center",
        end: "center bottom",
        onEnter: updateCounterValue,
        onEnterBack: updateCounterValue,
      },
    });

    animation.to([title, indicator], { color: theme.colors.red }, 0);
    animation.to(body, { color: theme.colors.black }, 0);
    timeline.add(animation);

    return () => {
      animation.kill();
    };
  }, [timeline, theme.colors.red, count, counterValueRef, theme.colors.black]);

  return (
    <Container ref={containerRef}>
      <Indicator ref={indicatorRef} as="span">{count}</Indicator>
      <Title ref={titleRef}>
        <Marker>&mdash;</Marker>
        {title}
      </Title>
      <Body ref={bodyRef}>{children}</Body>
    </Container>
  );
};

export default Paragraph;
